import { Form } from 'antd';
import { PurchaseDefaultEntity } from 'domain/entities/PurchaseEntity';
import { AppProps } from 'presentation/App';
import { AlertModalProps } from 'presentation/components/Modal/Modal.type';
import { openErrorNoti } from 'presentation/components/Notification';
import { PageTitle } from 'presentation/components/PageTitle';
import {
    completeFetching,
    showAlert,
    startFetching,
} from 'presentation/redux/features/app/appSlice';
import { useAppDispatch } from 'presentation/redux/hooks';
import { PRIVATE_ROUTES } from 'presentation/router/CONSTANTS';
import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MASTER_CATEGORY_LIST } from 'utilities/constants/COMMON';
import { commonHelpers } from 'utilities/helpers';
import { ButtonList } from '../CU-015-02/components/ButtonList';
import { StepViewOne } from './components/StepViewOne';
import { StepViewTwo } from './components/StepViewTwo';

const CU01602Page = (props: AppProps) => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const purchaseOfferId = searchParams.get('purchaseOfferId') || '';
    const listedPropertyId = searchParams.get('listedPropertyId') || '';
    const procedureId = searchParams.get('procedureId') || '';
    const purchasePrice = searchParams.get('purchasePrice') || '';
    const loanUseStatus = searchParams.get('loanUseStatus') || '';
    const [form] = Form.useForm();

    const dispatch = useAppDispatch();

    const [masterData, setMasterData] = React.useState<any>({});
    const [data, setData] = React.useState<PurchaseDefaultEntity>({});

    const { masterUseCase, purchaseUseCase } = props;

    useEffect(() => {
        (async () => {
            await Promise.all([initiateDefaultData(), initiateOptions()]);
        })();
    }, []);

    const initiateOptions = async () => {
        try {
            dispatch(startFetching());
            const options = await Promise.all([
                masterUseCase.getMasterSubCategoryList({
                    categoryCode: MASTER_CATEGORY_LIST.ADDRESSEE_DIV_CLASSIFICATION,
                }),
                masterUseCase.getMasterSubCategoryList({
                    categoryCode: MASTER_CATEGORY_LIST.DEAL_PURPOSE_TYPE_CLASSIFICATION,
                }),
            ]);
            const masterDataInit: any = {};
            masterDataInit[MASTER_CATEGORY_LIST.ADDRESSEE_DIV_CLASSIFICATION] = options[0]?.result
                ?.filter((item) => item.code == '30' || item.code == '99')
                .map((item: any) => {
                    return {
                        label: item.codeName,
                        value: item.code,
                    };
                });
            masterDataInit[MASTER_CATEGORY_LIST.DEAL_PURPOSE_TYPE_CLASSIFICATION] =
                options[1]?.result
                    ?.filter((item) => item.code !== '99')
                    .map((item: any) => {
                        return {
                            label: item.codeName,
                            value: item.code,
                        };
                    });
            setMasterData(masterDataInit);
        } catch (error: any) {
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    const subtract = (a?: any, b?: any) => {
        const aNumber = isNaN(Number(a)) ? 0 : Number(a);
        const bNumber = isNaN(Number(b)) ? 0 : Number(b);
        return aNumber - bNumber;
    };

    const initiateDefaultData = async () => {
        try {
            dispatch(startFetching());
            const response = await Promise.all([
                purchaseUseCase.getDefaultInfoCompany({
                    offerId: purchaseOfferId ? Number(purchaseOfferId) : 0,
                }),
                purchaseUseCase.getDefaultInfoCompanyStep2({
                    listedPropertyId: listedPropertyId ? Number(listedPropertyId) : 0,
                    purchasePrice: purchasePrice ? Number(purchasePrice) : 0,
                    offerId: purchaseOfferId ? Number(purchaseOfferId) : 0,
                }),
            ]);

            if (response) {
                const deposit = commonHelpers.toNumberOnly(response[1].result?.deposit) ?? 0;
                const finalPayment = subtract(response[1].result?.totalPrice, deposit);
                const mappingData = {
                    ...response[0].result,
                    ...response[1].result,
                    finalPayment: finalPayment,
                    procedureId: procedureId,
                    purchaseOfferId: purchaseOfferId,
                    listedPropertyId: listedPropertyId,
                    purchasePrice: purchasePrice,
                    loanUseStatus: loanUseStatus,
                    includeBrokerageFee: data.includeBrokerageFee ? [1] : [0],
                };
                setData(mappingData);
                form.setFieldsValue({
                    confirm_cic: '1',
                    includeBrokerageFee: mappingData.includeBrokerageFee,
                });
            }
        } catch (error: any) {
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    const back = () => {
        const param = {
            listedPropertyId: listedPropertyId,
            procedureId: procedureId,
            purchaseOfferId: purchaseOfferId,
            purchasePrice: purchasePrice,
            loanUseStatus: loanUseStatus,
            step: 2,
        };
        navigate(
            `${PRIVATE_ROUTES.PATH}/${
                PRIVATE_ROUTES.SUB.CU_016_01
            }?${commonHelpers.generateQueryString(param)}`,
        );
    };

    const submit = async () => {
        try {
            dispatch(startFetching());
            const payload = {
                procedureId: data?.procedureId ? Number(data?.procedureId) : 0,
                purchaseOfferId: data?.purchaseOfferId ? Number(data?.purchaseOfferId) : 0,
            };
            const response = await props.purchaseUseCase.registerMemberCompany(payload);
            if (response) {
                const alertProps: AlertModalProps = {
                    title: '購入申込を受け付けました',
                    message: '運営からの連絡をお待ちください。',
                    okText: 'ダッシュボードに戻る',
                    onOk: () => {
                        backToDashboard();
                    },
                };
                dispatch(showAlert(alertProps));
            }
        } catch (error: any) {
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    const backToDashboard = () => {
        navigate(`${PRIVATE_ROUTES.PATH}/${PRIVATE_ROUTES.SUB.DASHBOARD}`);
    };

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
            className="CU-016-01-container"
        >
            <PageTitle title="購入申込みフォーム" />
            <section
                style={{
                    padding: '10px 0px',
                    height: '100%',
                    width: '100%',
                    overflow: 'auto',
                    flexGrow: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    overflowX: 'hidden',
                }}
            >
                <Form id="top-form" form={form} className="purchase-form-container w-full md:w-1/2">
                    <div className="section-wrapper">
                        <StepViewOne data={data} masterData={masterData} />
                    </div>
                    <div className="section-wrapper">
                        <StepViewTwo data={data} masterData={masterData} />
                    </div>
                    <div className="section-wrapper">
                        <ButtonList next={submit} prev={back} />
                    </div>
                </Form>
            </section>
        </div>
    );
};

export default CU01602Page;
